/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orgSettingsService } from '@/services/organization/org-settings.service';
import { OrganizationSettingsDto, SettingDefinition } from '@/types/superadmin.types';
import { ApiError } from '@/services/api/types';
import { useAuthStore } from '@/stores/authStore';

export const myOrgSettingsKeys = {
  all: ['my-org-settings'] as const,
  settings: () => [...myOrgSettingsKeys.all, 'settings'] as const,
  available: () => [...myOrgSettingsKeys.all, 'available'] as const,
};

export function useMyOrgSettings() {
  const { user } = useAuthStore();
  const isOrgUser = !!user && user.role !== 'SUPER_ADMIN' && !!user.orgId;

  return useQuery<OrganizationSettingsDto, ApiError>({
    queryKey: myOrgSettingsKeys.settings(),
    queryFn: orgSettingsService.getSettings,
    enabled: isOrgUser,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateMyOrgSettings() {
  const queryClient = useQueryClient();

  return useMutation<OrganizationSettingsDto, ApiError, Record<string, string | null>>({
    mutationFn: (settings) => orgSettingsService.updateSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: myOrgSettingsKeys.all });
    },
  });
}

export function useMyAvailableSettings() {
  const { user } = useAuthStore();
  const isOrgUser = !!user && user.role !== 'SUPER_ADMIN' && !!user.orgId;

  return useQuery<SettingDefinition[], ApiError>({
    queryKey: myOrgSettingsKeys.available(),
    queryFn: orgSettingsService.getAvailableSettings,
    enabled: isOrgUser,
    staleTime: 10 * 60 * 1000,
  });
}
